import { useState, useEffect, useCallback } from 'react';
import { CookieConsent } from '@/types/cookies';
import { 
  getCookieConsent, 
  setCookieConsent, 
  hasConsentChoice, 
  clearCookieConsent,
  cleanupCookies,
  getDefaultConsent,
  getAcceptAllConsent
} from '@/utils/cookieConsent';

export const useCookieConsent = () => {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load consent on mount
  useEffect(() => {
    const loadConsent = () => {
      const storedConsent = getCookieConsent();
      setConsent(storedConsent);
      setShowBanner(!hasConsentChoice());
      setLoading(false);
    };

    loadConsent();

    // Listen for consent updates from other tabs
    const handleConsentUpdate = () => {
      loadConsent();
    };

    window.addEventListener('cookie-consent-updated', handleConsentUpdate);
    return () => {
      window.removeEventListener('cookie-consent-updated', handleConsentUpdate);
    };
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    const newConsent = getAcceptAllConsent();
    setCookieConsent(newConsent);
    setConsent({ ...newConsent, timestamp: Date.now(), version: '1.0' });
    setShowBanner(false);
  }, []);

  // Reject non-essential cookies
  const rejectAll = useCallback(() => {
    const newConsent = getDefaultConsent();
    setCookieConsent(newConsent);
    const fullConsent = { ...newConsent, timestamp: Date.now(), version: '1.0' };
    setConsent(fullConsent);
    setShowBanner(false);
    cleanupCookies(fullConsent);
  }, []);

  // Update specific consent preferences
  const updateConsent = useCallback((newConsent: Omit<CookieConsent, 'timestamp' | 'version'>) => {
    setCookieConsent(newConsent);
    const fullConsent = { ...newConsent, timestamp: Date.now(), version: '1.0' };
    setConsent(fullConsent);
    setShowBanner(false);
    cleanupCookies(fullConsent);
  }, []);

  // Clear all consent
  const clearConsent = useCallback(() => {
    clearCookieConsent();
    setConsent(null);
    setShowBanner(true);
  }, []);

  // Check if a specific category has consent
  const hasConsentFor = useCallback((category: keyof Omit<CookieConsent, 'timestamp' | 'version'>) => {
    return consent?.[category] || false;
  }, [consent]);

  return {
    consent,
    showBanner,
    loading,
    acceptAll,
    rejectAll,
    updateConsent,
    clearConsent,
    hasConsentFor,
    setShowBanner
  };
};
